import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useWhatsAppPopup } from '@/contexts/WhatsAppPopupContext';

// --- Sparkle particles scattered in background ---
const sparkles = [
    { x: '8%', y: '18%', size: 3, delay: '0s', dur: '2.8s' },
    { x: '22%', y: '72%', size: 2, delay: '0.6s', dur: '3.2s' },
    { x: '55%', y: '12%', size: 4, delay: '1.1s', dur: '2.5s' },
    { x: '78%', y: '65%', size: 2, delay: '1.7s', dur: '3.5s' },
    { x: '42%', y: '88%', size: 3, delay: '0.9s', dur: '2.9s' },
    { x: '88%', y: '28%', size: 2, delay: '0.3s', dur: '3.1s' },
    { x: '14%', y: '50%', size: 3, delay: '1.4s', dur: '2.6s' },
    { x: '68%', y: '92%', size: 2, delay: '0.7s', dur: '3.3s' },
];

// --- Animated Counter ---
const useCountUp = (end: number, duration: number, isVisible: boolean, suffix = '') => {
    const [count, setCount] = useState(0);
    const frameRef = useRef<number>();

    useEffect(() => {
        if (!isVisible) return;
        const startTime = performance.now();
        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(animate);
            }
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
    }, [end, duration, isVisible]);

    return count + suffix;
};

// --- Donut Chart SVG ---
const DonutChart: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
    const segments = [
        { label: 'Arrais Amador', percent: 55, color: 'hsl(222, 47%, 25%)' },
        { label: 'Motonauta', percent: 22, color: 'hsl(222, 47%, 45%)' },
        { label: 'Mestre Amador', percent: 15, color: 'hsl(222, 47%, 65%)' },
        { label: 'Capitão Amador', percent: 8, color: 'hsl(222, 47%, 80%)' },
    ];

    const radius = 80;
    const strokeWidth = 28;
    const circumference = 2 * Math.PI * radius;
    let accumulatedOffset = 0;

    return (
        <div className="flex flex-col items-center">
            {/* Glow ring behind the donut */}
            <div className="relative w-[220px] h-[220px]">
                <div
                    className="absolute inset-0 rounded-full animate-glow-pulse pointer-events-none"
                    style={{
                        background: 'radial-gradient(circle, hsl(43 74% 53% / 0.25) 0%, transparent 65%)',
                        filter: 'blur(16px)',
                        margin: '-10px',
                    }}
                />
                <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90 relative z-10">
                    {segments.map((seg, i) => {
                        const dashLength = (seg.percent / 100) * circumference;
                        const dashGap = circumference - dashLength;
                        const offset = accumulatedOffset;
                        accumulatedOffset += dashLength;

                        return (
                            <motion.circle
                                key={seg.label}
                                cx="100"
                                cy="100"
                                r={radius}
                                fill="none"
                                stroke={seg.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={`${dashLength} ${dashGap}`}
                                strokeDashoffset={-offset}
                                strokeLinecap="round"
                                initial={{ opacity: 0, strokeWidth: strokeWidth - 6 }}
                                animate={isVisible ? { opacity: 1, strokeWidth } : {}}
                                transition={{ duration: 0.7, delay: 0.3 + i * 0.18 }}
                                style={{ filter: `drop-shadow(0 0 6px ${seg.color}88)` }}
                            />
                        );
                    })}
                </svg>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-6">
                {segments.map((seg) => (
                    <div key={seg.label} className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: seg.color, boxShadow: `0 0 6px ${seg.color}` }}
                        />
                        <span className="text-xs text-primary-foreground/80">{seg.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Stat Card ---
const StatCard: React.FC<{
    title: string;
    value: number;
    suffix: string;
    description: string;
    color: string;
    isVisible: boolean;
    delay: number;
}> = ({ title, value, suffix, description, color, isVisible, delay }) => {
    const displayValue = useCountUp(value, 1.5, isVisible, suffix);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay }}
            whileHover={{ scale: 1.03, y: -3 }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-gold/40 transition-all duration-300 cursor-default"
            style={{ boxShadow: '0 4px 20px hsl(0 0% 0% / 0.15)' }}
        >
            <p className="text-xs uppercase tracking-widest text-primary-foreground/60 font-semibold mb-2">
                {title}
            </p>
            <p
                className="text-3xl sm:text-4xl font-bold mb-2 transition-all duration-500"
                style={{
                    color,
                    textShadow: isVisible
                        ? `0 0 20px ${color}88, 0 0 40px ${color}44`
                        : 'none',
                }}
            >
                {displayValue}
            </p>
            <p className="text-sm text-primary-foreground/70 leading-relaxed">{description}</p>
        </motion.div>
    );
};

// --- Main Section ---
const DadosMercadoSection: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const { openPopup } = useWhatsAppPopup();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    const stats = [
        {
            title: 'Nossa Aprovação',
            value: 98,
            suffix: '%',
            description: 'Reflexo da nossa metodologia focada em resultados e segurança.',
            color: '#22c55e',
        },
        {
            title: 'Validade da CHA',
            value: 10,
            suffix: ' Anos',
            description: 'Renovável sem necessidade de nova prova (para amadores até 65 anos).',
            color: '#a855f7',
        },
        {
            title: 'Tempo de Curso',
            value: 1,
            suffix: ' Dia',
            description: 'Duração média da aula prática obrigatória para Arrais.',
            color: '#3b82f6',
        },
        {
            title: 'Idade Mínima',
            value: 18,
            suffix: ' Anos',
            description: 'Completos na data do exame escrito.',
            color: '#f97316',
        },
    ];

    return (
        <section ref={sectionRef} className="py-24 sm:py-32 bg-primary relative overflow-hidden">

            {/* Dot grid pattern */}
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                    backgroundSize: '32px 32px',
                }}
            />

            {/* Liquid background blobs */}
            <div
                className="absolute -top-40 -right-40 w-[580px] h-[580px] pointer-events-none animate-blob"
                style={{
                    background: 'radial-gradient(circle, hsl(43 74% 53% / 0.12), transparent 65%)',
                    filter: 'blur(60px)',
                }}
            />
            <div
                className="absolute -bottom-40 -left-40 w-[500px] h-[500px] pointer-events-none animate-blob"
                style={{
                    background: 'radial-gradient(circle, hsl(222 84% 5% / 0.5), transparent 65%)',
                    filter: 'blur(50px)',
                    animationDelay: '6s',
                    animationDirection: 'reverse',
                }}
            />

            {/* Sparkle particles */}
            {sparkles.map((p, i) => (
                <div
                    key={i}
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        left: p.x,
                        top: p.y,
                        width: p.size,
                        height: p.size,
                        background: 'hsl(var(--gold))',
                        boxShadow: '0 0 6px 2px hsl(var(--gold) / 0.7)',
                        animation: `twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
                    }}
                />
            ))}

            <div className="container mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/20 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Dados de Mercado
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground">
                        O Cenário Náutico Brasileiro
                    </h2>
                    <p className="mt-4 text-lg text-primary-foreground/70 max-w-3xl mx-auto">
                        Entenda a distribuição de navegadores no Brasil. A grande maioria dos
                        alunos busca a habilitação para lazer em águas abrigadas.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">

                    {/* Donut Chart */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 flex flex-col items-center justify-center"
                        style={{ boxShadow: '0 0 40px hsl(0 0% 0% / 0.2), inset 0 0 30px hsl(255 255% 255% / 0.02)' }}
                    >
                        <h3 className="text-lg font-semibold text-primary-foreground mb-6">
                            Habilitações Emitidas
                        </h3>
                        <DonutChart isVisible={isVisible} />
                    </motion.div>

                    {/* Stat Cards Grid */}
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {stats.map((stat, i) => (
                            <StatCard
                                key={stat.title}
                                {...stat}
                                isVisible={isVisible}
                                delay={0.3 + i * 0.12}
                            />
                        ))}
                    </div>

                </div>

                {/* Market Data CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="mt-16 p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md text-center max-w-4xl mx-auto"
                >
                    <h4 className="text-xl sm:text-2xl font-bold text-primary-foreground mb-4">
                        O mar te espera. Comece sua jornada hoje mesmo.
                    </h4>
                    <p className="text-primary-foreground/60 mb-8 max-w-2xl mx-auto">
                        Com nossa metodologia testada e 98% de aprovação, você conquista sua habilitação de forma rápida, segura e sem estresse burocrático.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button onClick={() => openPopup("escola")} className="bg-gold text-primary font-bold px-8 py-4 rounded-lg hover:bg-gold/90 transition-all shadow-lg hover:shadow-gold/20 transform hover:-translate-y-1">
                            Reservar minha Vaga na Próxima Turma
                        </button>
                        <button className="text-primary-foreground/80 hover:text-primary-foreground font-medium px-8 py-4 border border-white/20 rounded-lg transition-all">
                            Falar com Instrutor
                        </button>
                    </div>
                    <p className="mt-4 text-[10px] text-primary-foreground/40 uppercase tracking-widest">
                        Vagas limitadas para as próximas aulas práticas
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default DadosMercadoSection;
